import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Target, Heart, Users, Award, PawPrint, ArrowRight, CheckCircle2 } from 'lucide-react';
import { siteImages } from '../data/mock';

const OurMissionPage = () => {
  useEffect(() => {
    document.title = 'Our Mission | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img
            src={siteImages.heroLifestyle}
            alt="Our Mission"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <Target className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Our Purpose</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                Our Mission
              </h1>
              <p className="text-brand-cream max-w-lg">
                Helping pet parents give their furry family the comfort they deserve.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission Statement */}
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12 mb-16 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-brand-orange to-brand-orange-dark rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <PawPrint className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl text-brand-brown font-extrabold mb-6" className-FONT-DISPLAY>
              Happier Pets, Easier Days for Pet Parents
            </h2>
            <p className="text-brand-brown-light text-lg leading-relaxed max-w-3xl mx-auto">
              At Paw Supplies Group, our mission is simple: to put thoughtfully chosen pet accessories in the hands of every pet parent in the U.S.A. We believe that the right bed, bowl, toy or leash can make daily care easier and your pet&apos;s day a little brighter &mdash; and we&apos;re dedicated to curating only the items we&apos;d use for our own pets.
            </p>
          </div>

          {/* Core Values */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-brand-cream text-brand-orange-dark text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-brand-divider">
                Core Values
              </span>
              <h2 className="text-3xl lg:text-4xl text-brand-brown font-extrabold" className-FONT-DISPLAY>
                What We Stand For
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Excellence', desc: 'We aim for excellence in every product we offer and every interaction with you.' },
                { icon: Heart, title: 'Love-Led', desc: 'We love pets, and that love guides every recommendation we make.' },
                { icon: Users, title: 'Community', desc: 'We&apos;re building a community of pet parents who share tips, joys and tail wags.' },
                { icon: Award, title: 'Quality', desc: 'We never compromise on quality &mdash; every pick meets a pet-first standard.' },
              ].map((value, i) => (
                <div key={i} className="bg-brand-cream border border-brand-divider rounded-2xl p-6 hover:border-brand-orange/40 transition-all group">
                  <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-orange-light/50 transition-colors">
                    <value.icon className="w-7 h-7 text-brand-orange-dark" />
                  </div>
                  <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>{value.title}</h3>
                  <p className="text-brand-brown-light text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Our Promise */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <div className="aspect-video rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={siteImages.powerBackground}
                  alt="Our Promise"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <span className="inline-block px-4 py-1.5 bg-brand-cream text-brand-orange-dark text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-brand-divider">
                Our Promise
              </span>
              <h2 className="text-3xl text-brand-brown font-extrabold mb-6" className-FONT-DISPLAY>
                We&apos;re Here for Every Pet Parent
              </h2>
              <p className="text-brand-brown-light leading-relaxed mb-6">
                Whether you&apos;re welcoming a brand new puppy or upgrading the home of a senior cat, we&apos;re here to support you. Our team is dedicated to helping you choose the right pet accessories &mdash; without the overwhelm of a giant pet superstore.
              </p>
              <div className="space-y-4">
                {[
                  'Premium pet accessories and equipment',
                  'Free shipping across the U.S.A.',
                  '30-day hassle-free returns',
                  'Friendly customer support',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-brand-brown">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-brand-cream border border-brand-divider rounded-3xl p-12">
            <h2 className="text-3xl font-extrabold text-brand-brown mb-4" className-FONT-DISPLAY>
              Join the Paw Supplies Group Family
            </h2>
            <p className="text-brand-brown-light mb-8 max-w-xl mx-auto">
              Start treating your pet to accessories that fit the way they really live.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange transition-all shadow-lg"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OurMissionPage;
